<script setup lang="ts">
import { Store } from 'lucide-vue-next'
import type { MarketingFlow } from '@/views/autopilot/MarketingFlowPage.vue'
import type { RowsInterface } from '@/components/ui/Table/interfaces'
import { coversAllUserStores } from '@/views/autopilot/marketingFlowStores'
import { getRowContentCreator, getRowContentScope } from '@/utils/contentScope'

const props = withDefaults(
  defineProps<{
    marketingFlows: MarketingFlow[]
    currentPage: number
    lastPage: number
    total: number
    userStoreIds: number[]
    isFetching: boolean
    paginationEnabled?: boolean
    withOrigin?: boolean
  }>(),
  {
    currentPage: 1,
    total: 1,
    lastPage: 1,
    userStoreIds: () => [],
    isFetching: false,
    paginationEnabled: true,
    withOrigin: false
  }
)
const { t } = useI18n()
const router = useRouter()
const { formatNumber, formatPercentage } = useNumber()

const columns = computed(() => ({
  name: {
    label: t('pages.autopilot.autopilotsPage.table.columns.scenario'),
    headerClass: 'w-auto! flex-1!'
  },
  status: {
    label: t('pages.autopilot.autopilotsPage.table.columns.status'),
    headerClass: 'w-auto!'
  },
  ...(props.withOrigin && {
    origin: {
      label: t('contentScope.origin'),
      headerClass: 'w-auto!'
    }
  }),
  stores: {
    label: t('pages.autopilot.autopilotsPage.table.columns.store'),
    headerClass: 'w-auto!'
  },
  executions: {
    label: t('pages.autopilot.autopilotsPage.table.columns.executions'),
    headerClass: 'w-auto!',
    numeric: true
  },
  clicks: {
    label: t('pages.autopilot.autopilotsPage.table.columns.clicks'),
    headerClass: 'w-auto!',
    numeric: true
  },
  visits: {
    label: t('pages.autopilot.autopilotsPage.table.columns.visits'),
    headerClass: 'w-auto!',
    numeric: true
  },
  revenue: {
    label: t('pages.autopilot.autopilotsPage.table.columns.revenueGenerated'),
    headerClass: 'w-auto!',
    numeric: true
  },
  ...(props.withOrigin && {
    creator: {
      label: t('contentScope.filters.createdBy'),
      headerClass: 'w-auto!'
    }
  })
}))

const goToDetails = ({ id, data }) => {
  if (data.triggerTemplate) {
    router.push({ name: 'MarketingFlowCreate', query: { fromTemplate: id } })
  } else {
    router.push({ name: 'MarketingFlowDetail', params: { marketingFlowId: id }, query: { tab: 'preview' } })
  }
}

const rows = computed(() => {
  return [
    ...(props.marketingFlows
      ? props.marketingFlows.map((flow) => ({
          id: flow.id,
          publicId: flow.public_id,
          data: {
            triggerTemplate: '',
            name: flow.name,
            scope: flow.scope ?? null,
            creator: flow.creator ?? null,
            status: !flow.is_draft ? 'active' : 'inactive',
            clicks: flow.statistics.click_conversion_percentage
              ? formatPercentage(flow.statistics.click_conversion_percentage, 0)
              : null,
            clickCount: flow.statistics.count_click_conversions || '-',
            stores: flow.stores,
            revenue: flow.statistics?.revenue_formatted || '-',
            visits: flow.statistics.visit_conversion_percentage
              ? formatPercentage(flow.statistics.visit_conversion_percentage, 0)
              : null,
            visitCount: flow.statistics.count_visit_conversions || '-',
            executions: flow.statistics.count_messages ? formatNumber(flow.statistics.count_messages) : '-'
          }
        }))
      : [])
  ] as RowsInterface[]
})

const pagination = computed(() => ({
  currentPage: props.currentPage,
  total: props.total,
  lastPage: props.lastPage
}))

const emit = defineEmits(['change-page'])
const getData = (page) => {
  emit('change-page', page)
}
</script>

<template>
  <AppTable
    :columns="columns"
    :rows="rows"
    row-classes="cursor-pointer"
    :pagination="paginationEnabled ? pagination : null"
    :is-loading="isFetching"
    @update:change-page="getData"
    @row-clicked="goToDetails"
  >
    <template #th-executions="{ th }">
      <p class="flex flex-row-reverse items-center gap-2 leading-[20px]">
        <span>{{ th.label }}</span>
        <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-muted-foreground" />
      </p>
    </template>

    <template #th-clicks="{ th }">
      <p class="flex flex-row-reverse items-center gap-2 leading-[20px]">
        <span>{{ th.label }}</span>
        <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-muted-foreground" />
      </p>
    </template>
    <template #th-revenue="{ th }">
      <p class="flex flex-row-reverse items-center gap-2 leading-[20px]">
        <span>{{ th.label }}</span>
        <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-muted-foreground" />
      </p>
    </template>
    <template #th-visits="{ th }">
      <p class="flex flex-row-reverse items-center gap-2 leading-[20px]">
        <span>{{ th.label }}</span>
        <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-muted-foreground" />
      </p>
    </template>

    <template #td-name="{ data }">
      <span class="block max-w-[40ch] truncate whitespace-nowrap font-medium" :title="data.name">{{ data.name }}</span>
    </template>

    <template #th-origin>
      <ContentScopeColumnHeader subject="automation" />
    </template>
    <template #td-origin="{ data }">
      <ContentScopeBadge :scope="getRowContentScope(data)" />
    </template>

    <template #td-creator="{ data }">
      <ContentScopeCreator :creator="getRowContentCreator(data)" />
    </template>

    <template #td-status="{ data }">
      <div class="flex items-center py-2">
        <BaseChip
          :color="data.status === 'active' ? 'green' : 'yellow'"
          :text="
            data.status === 'active'
              ? t('pages.autopilot.autopilotsPage.items.active')
              : t('pages.autopilot.autopilotsPage.items.inactive')
          "
        />
      </div>
    </template>

    <template #td-stores="{ data }">
      <div class="flex items-center">
        <AppTooltip v-if="data.stores.length > 0" tooltip-position="bottom">
          <template #tooltip-content>
            <div class="flex flex-col gap-2 px-1 text-left">
              <span v-for="store in data.stores" :key="store.id" class="text-md">
                {{ store.name }}
              </span>
            </div>
          </template>
          <BaseChip
            :text="
              coversAllUserStores(data.stores, userStoreIds)
                ? t('pages.autopilot.autopilotsPage.items.allStores')
                : t('pages.autopilot.autopilotsPage.items.store', { count: data.stores.length })
            "
          >
            <template #leading>
              <Store />
            </template>
          </BaseChip>
        </AppTooltip>
        <p v-else>-</p>
      </div>
    </template>

    <template #empty-state>
      <slot name="empty-state" />
    </template>

    <template #td-clicks="{ data }">
      <p>{{ formatNumber(data.clickCount) }}</p>
      <p v-if="data.clicks" class="mt-1 text-xs font-normal text-muted-foreground">
        {{ data.clicks }}
      </p>
    </template>

    <template #td-visits="{ data }">
      <p>{{ formatNumber(data.visitCount) }}</p>
      <p v-if="data.visits" class="mt-1 text-xs font-normal text-muted-foreground">
        {{ data.visits }}
      </p>
    </template>
  </AppTable>
</template>
